/* @flow */
import get from 'lodash/get';

import {
  upsertCartProduct,
  type ProductId,
  type Product,
  type ProductData,
  type Tax,
} from '@gandi/cart';

import { notifyDanger } from '@gandi/notifications';
import type { FetchResponse } from '@gandi/resource/redux';

import type { TranslatorProps } from '@gandi/translate';

import { SubmissionError } from 'gandi.react-form';

import type { GandiLogger } from 'gandi.redux-logger';

import type { Dispatch } from '../../types';

// ---------------------------------------------------------------- Define types

// custom types for api fields and form
export type APIFieldName = string | Array<string>;
export type FormFieldName = string;

type APIError = {
  name: APIFieldName,
  description: string,
  ...
};

type Contract =
  | {
      href: string,
      identifier?: string,
      name: string,
      ...
    }
  | {
      href?: string,
      identifier: string,
      name: string,
      ...
    };

type CreateProductResponseSuccess = FetchResponse<
  202,
  {
    id: string,
    message: string,
    contracts: Array<Contract>,
    prices: Array<Contract>,
    status: string,
    total: {
      before_taxes: number,
      tax_amount: number,
      after_taxes: number,
      ...
    },
    payload: any,
    ...
  },
>;
type UpdateProductResponseSuccess = FetchResponse<
  202,
  {
    message: string,
    contracts: Array<{ href?: string, identifier?: string, name: string, ... }>,
    prices: Array<{
      description: string,
      taxes: Array<Tax>,
      base_price: number,
      ...
    }>,
    status: string,
    total: {
      before_taxes: number,
      tax_amount: number,
      after_taxes: number,
      ...
    },
    payload: any,
    ...
  },
>;

export type UpsertProductResponseSuccess =
  | CreateProductResponseSuccess
  | UpdateProductResponseSuccess;
export type UpsertProductResponseFailure = FetchResponse<
  422,
  { message: string, errors?: Array<APIError>, status: 'error', ... },
>;

// ---------------------------------------------------------------- Custom error

/**
 * Thanks to http://stackoverflow.com/q/31089801/1070271
 *
 * Custom error to handle errors during product upsert in cart.
 */
class UpsertProductError extends Error {
  response: ?UpsertProductResponseFailure;
  apiErrors: Array<APIError>;

  constructor(message: string, response: ?UpsertProductResponseFailure) {
    super(message);
    this.response = response;
    this.apiErrors =
      response && response.data && response.data.errors
        ? response.data.errors
        : [];
  }

  /**
   * Check if response contains errors related to given api field names.
   *
   * example:
   *
   * ``` javascript
   * // check if there is an error related to language or database selection:
   * err.hasExpectedErrors(['type.language.name', 'type.database.value']);
   * ```
   */
  hasExpectedErrors(apiFieldnames: Array<APIFieldName>): boolean {
    return (
      (this.apiErrors || []).filter(err => apiFieldnames.indexOf(err.name) >= 0)
        .length > 0
    );
  }

  /**
   * Map errors from api fields to form fields.
   *
   * example:
   *
   * ``` javascript
   * err.toFormErrors({
   *   language: 'type.language.name',
   *   database: 'type.database.value',
   * });
   * ```
   *
   * will return something as:
   *
   * ``` javascript
   * {
   *   language: 'This is not a valid language.',
   *   database: 'This is invalid',
   * }
   * ```
   */
  toFormErrors(formToApiMap: { [k: FormFieldName]: APIFieldName, ... } = {}): {
    [FormFieldName | APIFieldName]: string,
    ...
  } {
    // swap formToApiMap
    const apiToFormMap: { [APIFieldName]: FormFieldName, ... } = Object.keys(
      formToApiMap,
    ).reduce(
      (
        acc: { [APIFieldName]: FormFieldName, ... },
        key: FormFieldName,
      ): {
        [APIFieldName]: FormFieldName,
        ...
      } => {
        const result = { ...acc };
        if (Array.isArray(formToApiMap[key])) {
          formToApiMap[key].forEach(x => {
            result[x] = key;
          });
        } else {
          result[formToApiMap[key]] = key;
        }
        return result;
      },
      {},
    );

    // return form validation errors
    return this.apiErrors.reduce(
      (
        acc: { [FormFieldName | APIFieldName]: string, ... },
        error: APIError,
      ): { [FormFieldName | APIFieldName]: string, ... } => {
        const translatedKey = apiToFormMap[error.name] || error.name;
        return {
          ...acc,
          // $FlowFixMe[invalid-computed-prop]
          [translatedKey]: error.description,
        };
      },
      {},
    );
  }
}

/**
 * Create or update given product.
 * Takes care of cart initialization logic.
 *
 * In case of error, it rejects a `UpsertProductError`, so you can leverage
 * some utilities for error parsing.
 */
type UpsertProductArgs = {
  productData: Product | ProductData,
  productId: ?ProductId,
};

const upsertProduct =
  ({
    productData,
    productId,
  }: UpsertProductArgs): (Dispatch => Promise<
    UpsertProductResponseSuccess | UpsertProductError,
  >) =>
  dispatch =>
    Promise.resolve()
      .then(() =>
        dispatch(
          // $FlowFixMe[prop-missing]
          upsertCartProduct({
            // $FlowFixMe[incompatible-exact]
            // $FlowFixMe[prop-missing]
            // $FlowFixMe[incompatible-call]
            productData,
            // $FlowFixMe to refine
            productId,
          }),
        ),
      )
      .then(response => {
        if (response.data.status !== 'error') return response;

        const errors = response.data.errors;
        if (!Array.isArray(errors) || !errors.length) {
          // status is error, but there is no associated error
          throw new Error(
            'addProduct response status is "error" but there is no associated error',
          );
        }

        throw new UpsertProductError('Product dry-run failed', response);
      });

// ---------------------------------------------------------- Redux-form helpers

/**
 * Compute initial values from product.
 *
 * Given a map of field names, a product and default values (generally taken
 * from location.query), it returns form initial values.
 *
 * Note that field mapping is defined in an object. We always use form field
 * name as key, to provide a consistent API.
 */
const computeInitialValues = (
  formToApiMap: { [k: FormFieldName]: APIFieldName, ... } = {},
  // $FlowIgnore[incompatible-type]
  product: Product | ProductData = {},
  defaults: { [k: FormFieldName]: any, ... } = {},
): { [k: FormFieldName]: any, ... } =>
  Object.keys(formToApiMap).reduce((acc, key) => {
    const productField = formToApiMap[key];
    // Some form fields can map to multiple API product fields
    // In such cases, use the first truthy value available.
    if (Array.isArray(productField)) {
      // $FlowIgnore[prop-missing]
      acc[key] = productField.reduce(
        (firstTruthy, arrayKey) =>
          firstTruthy || get(product.payload, arrayKey, defaults[arrayKey]),
        undefined,
      );
    } else {
      // $FlowIgnore[prop-missing]
      acc[key] = get(product.payload, productField, defaults[key]);
    }
    return acc;
  }, {});

/**
 * Create a function that handle `upsertProduct` rejection for form.
 *
 * It transforms `UpsertProductError` into a `SubmissionError`, log a message
 * if something strange happens and notify user with API message.
 *
 * Note that field mapping is defined in an object. We always use form field
 * name as key, to provide a consistent API.
 */
const createUpsertErrorToSubmissionError =
  (
    formToApiMap: { [k: FormFieldName]: APIFieldName, ... },
    paramLogger: GandiLogger,
  ): ((
    SubmissionError | UpsertProductError | Error,
    Dispatch,
    { translator: TranslatorProps, ... },
  ) => UpsertProductResponseSuccess) =>
  (err, dispatch, props: { translator: TranslatorProps, ... }) => {
    // Do not alter SubmissionError already created by `onSubmit`
    if (err instanceof SubmissionError) throw err;

    // Extract valid/expected api error fields in an array
    const expectedErrorFields = Object.keys(formToApiMap || {}).reduce(
      (acc: Array<APIFieldName>, key: FormFieldName) =>
        acc.concat(formToApiMap[key]),
      [],
    );

    // If there is expected error fields, then we turn it into a SubmissionError.
    // The cart validation (aka dryrun) failed.
    // Those fields will be displayed in form fields.
    if (
      // $FlowFixMe[method-unbinding]
      typeof err.hasExpectedErrors === 'function' &&
      // $FlowFixMe[method-unbinding]
      typeof err.toFormErrors === 'function' &&
      // $FlowFixMe: flow doesn't know hasExpectedErrors
      err.hasExpectedErrors(expectedErrorFields)
    ) {
      // $FlowFixMe: flow doesn't know toFormErrors
      throw new SubmissionError(err.toFormErrors(formToApiMap));
    }

    // There is only errors associated to unknown fields OR this is an engine
    // Error.
    // We log this and return a success as this is the cart overview
    // responsibility to display product global errors.
    // $FlowFixMe[method-unbinding]
    if (typeof err.toFormErrors === 'function') {
      // Validation failed with api errors not related to current form.
      dispatch(
        paramLogger.warn(
          // $FlowFixMe: flow doesn't know toFormErrors
          { err, unexpectedErrors: err.toFormErrors() },
          '[createOnSubmitFail] Strange validation behavior.',
        ),
      );

      // There is some erorrs, or we would not have a UpsertProductError object.
      // This is the cart overview responsibility to display global errors, so we
      // follow success path.
      if (
        err instanceof UpsertProductError &&
        err.response &&
        // $FlowFixMe[incompatible-type]
        err.response.status === 201
      )
        return err.response;
    }

    // this is not an UpsertCartError.
    dispatch(
      paramLogger.error({ err }, '[createOnSubmitFail] Unexpected error.'),
    );

    // Note that default API message is 'Resource Created' or 'Request Accepted'.
    // As this is no error to display for user, we attempt to display the first
    // error message that is not internal.
    dispatch(
      notifyDanger(
        get(
          err,
          'response.data.message',
          props.translator.__('Unexpected server error.'),
        ),
      ),
    );

    // reject form anyway
    const submitError = new SubmissionError({});
    // $FlowFixMe[prop-missing]
    submitError.originalError = err;
    throw submitError;
  };

export {
  UpsertProductError,
  upsertProduct,
  computeInitialValues,
  createUpsertErrorToSubmissionError,
};
